<template>
  <div class="table-container">
    <h3 class="table-title">Enter Your Draw Points</h3>
    <p class="subtitle">
      Enter your Utah points for each species and press Save. Leave Points blank for species you don't track.
    </p>

    <div v-if="isInitializing" class="text-center my-4">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <form v-else @submit.prevent="savePoints">
      <table class="table">
        <thead>
          <tr>
            <th>Species</th>
            <th>Next Eligible Year</th>
            <th>Points</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.species">
            <td>{{ row.species }}</td>
            <td>
              <input v-model="row.nextEligibleYear" type="number" min="2000" max="2100" step="1"
                class="form-control form-control-sm cell-input" :aria-label="row.species + ' next eligible year'"
                placeholder="Optional" @input="clearMessages" />
            </td>
            <td>
              <input v-model="row.points" type="number" min="0" max="99" step="1"
                class="form-control form-control-sm cell-input" :class="{ 'is-invalid': rowError(row) }"
                :aria-label="row.species + ' points'" @input="clearMessages" />
              <div v-if="rowError(row)" class="invalid-feedback">{{ rowError(row) }}</div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="d-flex justify-content-between align-items-center mt-3">
        <button type="button" class="btn btn-link" @click="$emit('showHome')">Back to home</button>
        <button type="submit" class="btn btn-primary" :disabled="isSaving || hasErrors">
          {{ isSaving ? 'Saving...' : 'Save Points' }}
        </button>
      </div>

      <p v-if="errorMessage" class="error-text mt-3">{{ errorMessage }}</p>
      <p v-if="successMessage" class="success-text mt-3">{{ successMessage }}</p>
    </form>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { SPECIES_LIST } from '../constants/species';

// Everything is Utah for now; the API also stores a state per row.
const STATE = 'UTAH';

export default {
  name: 'ManualPointsView',

  data() {
    return {
      rows: [],
      isInitializing: false,
      isSaving: false,
      errorMessage: '',
      successMessage: '',
    };
  },

  computed: {
    ...mapState({
      drawPoints: state => state.drawPoints,
    }),
    hasErrors() {
      return this.rows.some(row => this.rowError(row));
    },
  },

  created() {
    this.initialize();
  },

  methods: {
    ...mapActions(['setDrawPoints']),

    async initialize() {
      if (!this.drawPoints) {
        this.isInitializing = true;
        try {
          const response = await fetch(this.$API_BASE_URL + '/api/drawpoints', {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          });
          if (response.ok) {
            const data = await response.json();
            this.setDrawPoints(data.drawPointsList);
          }
        } catch (error) {
          console.error('Error fetching draw points:', error);
        } finally {
          this.isInitializing = false;
        }
      }
      this.initRows(this.drawPoints || []);
    },

    // One row per species, filled in from the customer's saved points. Saved species that
    // aren't in the standard list are kept so they can still be edited or cleared.
    initRows(pointsList) {
      const saved = new Map(pointsList.map(item => [item.species, item]));
      const species = [...SPECIES_LIST, ...pointsList.map(item => item.species).filter(s => !SPECIES_LIST.includes(s))];
      this.rows = species.map(name => {
        const item = saved.get(name);
        const points = item && item.points != null ? String(item.points) : '';
        const year = item ? this.parseYear(item.nextEligible) : null;
        const nextEligibleYear = year != null ? String(year) : '';
        return { species: name, id: item ? item.id : null, points, nextEligibleYear, original: { points, nextEligibleYear } };
      });
    },

    parseYear(value) {
      const parsed = parseInt(value, 10);
      return isNaN(parsed) || parsed === 0 ? null : parsed;
    },

    clearMessages() {
      this.errorMessage = '';
      this.successMessage = '';
    },

    rowError(row) {
      const points = String(row.points).trim();
      if (points !== '') {
        const value = Number(points);
        if (!Number.isInteger(value) || value < 0 || value > 99) return 'Points must be a whole number from 0 to 99.';
      }
      const year = String(row.nextEligibleYear).trim();
      // A row with no points isn't saved, so its year doesn't matter.
      if (year !== '' && points !== '') {
        const value = Number(year);
        if (!Number.isInteger(value) || value < 2000 || value > 2100) return 'Enter a four-digit year.';
      }
      return '';
    },

    isChanged(row) {
      return String(row.points).trim() !== row.original.points
        || String(row.nextEligibleYear).trim() !== row.original.nextEligibleYear;
    },

    async savePoints() {
      if (this.hasErrors || this.isSaving) return;
      this.clearMessages();

      const changed = this.rows.filter(row => this.isChanged(row));
      // Clearing the points of a saved species removes it.
      const toDelete = changed.filter(row => row.id != null && String(row.points).trim() === '');
      const toSave = changed.filter(row => String(row.points).trim() !== '');

      if (toDelete.length === 0 && toSave.length === 0) {
        this.successMessage = 'No changes to save.';
        return;
      }

      this.isSaving = true;
      try {
        let pointsList = this.drawPoints || [];

        for (const row of toDelete) {
          const response = await fetch(this.$API_BASE_URL + '/api/drawpoints/' + row.id, {
            method: 'DELETE',
            credentials: 'include',
          });
          if (!response.ok) {
            this.errorMessage = await this.extractErrorMessage(response);
            return;
          }
          pointsList = pointsList.filter(item => item.id !== row.id);
        }

        if (toSave.length > 0) {
          const response = await fetch(this.$API_BASE_URL + '/api/drawpoints/manual', {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(toSave.map(row => this.toPayload(row))),
          });
          if (!response.ok) {
            this.errorMessage = await this.extractErrorMessage(response);
            return;
          }
          // The response is the customer's full points list, the same shape GET /api/drawpoints returns.
          pointsList = (await response.json()).drawPointsList || [];
        }

        this.setDrawPoints(pointsList);
        this.initRows(pointsList);
        this.successMessage = 'Points saved.';
      } catch (error) {
        console.error('Error saving points:', error);
        this.errorMessage = 'Failed to save points. Please try again later.';
      } finally {
        this.isSaving = false;
      }
    },

    toPayload(row) {
      const payload = { state: STATE, species: row.species, points: Number(row.points) };
      // Omitted years leave the stored value untouched.
      const year = String(row.nextEligibleYear).trim();
      if (year !== '') payload.nextEligibleYear = Number(year);
      return payload;
    },

    async extractErrorMessage(response) {
      try {
        const text = await response.text();
        if (!text) return 'Request failed (' + response.status + '). Please try again.';
        try {
          const body = JSON.parse(text);
          return body.message || body.error || text;
        } catch {
          return text;
        }
      } catch {
        return 'Request failed (' + response.status + '). Please try again.';
      }
    },
  },
};
</script>

<style scoped>
/* Matches DrawPointsTable so the edit view reads as the same table. */
.table-container {
  max-width: 800px;
  margin: 20px auto;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.table-title {
  text-align: center;
  margin-bottom: 10px;
  font-weight: bold;
  font-size: 1.5rem;
  color: #333;
}

.subtitle {
  text-align: center;
  color: #666;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table thead {
  background-color: lightblue;
  color: black;
}

.table th,
.table td {
  text-align: left;
  padding: 10px;
  border: 1px solid #ddd;
  vertical-align: middle;
}

.table tbody tr:nth-child(even) {
  background-color: #f2f2f2;
}

.cell-input {
  max-width: 120px;
}

.error-text {
  color: red;
  font-size: 14px;
}

.success-text {
  color: green;
  font-size: 16px;
}
</style>
